// The Workshop's lit stages for still pictures. The store's preview pictures are renders on a flat grey ground; on a
// grey tile they read as grey squares (the open-haunt review). Here each picture is cut out of its ground in the
// browser (the ground colour is read from the corners and keyed out with a soft edge), cropped to the model and set on
// a stage drawn in CSS: a backdrop in the zone's surface colour, a key light, a rim light and a pool of light on the
// floor. Same-origin pictures only (the package mount), so the canvas stays readable.

import { h } from "../../dom.ts";

/** Pixels within this distance of the ground colour are ground; up to `SOFT` more fade in (a soft, anti-aliased edge). */
const HARD = 10;
const SOFT = 26;
/** The longest side a cut-out is drawn at (tiles are at most ~360 css px; 2x for sharp screens). */
const MAX = 720;

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Key out a flat ground: alpha from each pixel's distance to the ground colour (the median of the four corners).
 * Returns the bounding box of what's left, or null when nothing is (a blank picture). Pure, for the tests.
 */
export function keyGround(px: Uint8ClampedArray, w: number, hgt: number): Box | null {
  const corner = (x: number, y: number) => (y * w + x) * 4;
  const cs = [corner(0, 0), corner(w - 1, 0), corner(0, hgt - 1), corner(w - 1, hgt - 1)];
  const ground = [0, 1, 2].map((c) => {
    const v = cs.map((i) => px[i + c]!).sort((a, b) => a - b);
    return (v[1]! + v[2]!) / 2;
  });
  let x0 = w, y0 = hgt, x1 = -1, y1 = -1;
  for (let y = 0; y < hgt; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const d = Math.max(Math.abs(px[i]! - ground[0]!), Math.abs(px[i + 1]! - ground[1]!), Math.abs(px[i + 2]! - ground[2]!));
      const a = d <= HARD ? 0 : d >= HARD + SOFT ? 1 : (d - HARD) / SOFT;
      px[i + 3] = Math.round(px[i + 3]! * a);
      if (a > 0.5) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  return x1 < 0 ? null : { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

/** Pad a box by a share of its size, kept inside the picture. */
export function padBox(b: Box, w: number, hgt: number, pad = 0.12): Box {
  const p = Math.round(Math.max(b.w, b.h) * pad);
  const x = Math.max(0, b.x - p), y = Math.max(0, b.y - p);
  return { x, y, w: Math.min(w, b.x + b.w + p) - x, h: Math.min(hgt, b.y + b.h + p) - y };
}

/** Swap a loaded picture for its cut-out (a canvas). Leaves the picture as it was if anything goes wrong. */
function cutOut(img: HTMLImageElement): void {
  try {
    const w = img.naturalWidth, hgt = img.naturalHeight;
    if (!w || !hgt) return;
    const scale = Math.min(1, MAX / Math.max(w, hgt));
    const sw = Math.round(w * scale), sh = Math.round(hgt * scale);
    const work = document.createElement("canvas");
    work.width = sw;
    work.height = sh;
    const g = work.getContext("2d", { willReadFrequently: true });
    if (!g) return;
    g.drawImage(img, 0, 0, sw, sh);
    const data = g.getImageData(0, 0, sw, sh);
    const box = keyGround(data.data, sw, sh);
    if (!box) return;
    g.putImageData(data, 0, 0);
    const b = padBox(box, sw, sh);
    const out = document.createElement("canvas");
    out.width = b.w;
    out.height = b.h;
    out.getContext("2d")!.drawImage(work, b.x, b.y, b.w, b.h, 0, 0, b.w, b.h);
    out.className = `${img.className} cut`;
    out.setAttribute("aria-hidden", "true");
    img.replaceWith(out);
  } catch {
    /* a tainted or broken picture: keep it */
  }
}

/**
 * A lit stage holding one picture. `light`: "lamp" (warm key, cool rim) or "moon" (a cold moonlit gradient: fog, bats
 * and ghosts are pale and need dark blue behind them to read). The picture loads lazily and is cut out once loaded.
 */
export function litStage(src: string, light: "lamp" | "moon" = "lamp", extra = ""): HTMLElement {
  const img = h("img", { class: "ws-pic", src, alt: "", loading: "lazy", decoding: "async" });
  const stage = h("span", { class: `ws-stage ${light} ${extra}`.trim(), "aria-hidden": "true" }, h("span", { class: "ws-pool" }), img, h("span", { class: "ws-shimmer" }));
  const done = () => {
    stage.classList.add("ready");
    const idle = (window as unknown as { requestIdleCallback?: (f: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
    if (idle) idle(() => cutOut(img), { timeout: 800 });
    else window.setTimeout(() => cutOut(img), 50);
  };
  img.addEventListener("load", done, { once: true });
  img.addEventListener("error", () => stage.classList.add("ready", "no-pic"), { once: true });
  return stage;
}

/** Pale things that need the moon behind them. */
export const MOONLIT = /fog|bats|ghost/;
